import { Badge, Divider, Group, Paper, Progress, Stack, Text } from "@mantine/core";
import useGetAvaliacoesIA from "../../hooks/useGetAvaliacoesIA";

type AvaliacaoIAModalContentProperties = {
  ideia: TIdeia;
};

const STATUS_CONFIG: Record<TAvaliacaoIA["status"], { color: string; label: string }> = {
  COMPLETED: { color: "green", label: "Finalizada" },
  FAILED: { color: "red", label: "Falha" },
  PENDING: { color: "yellow", label: "Pendente" }
};

/**
 *
 * @param root0
 * @param root0.ideia
 */
function AvaliacaoIAModalContent({ ideia }: AvaliacaoIAModalContentProperties): JSX.Element {
  const { data: avaliacao, isLoading, error } = useGetAvaliacoesIA(ideia);

  if (isLoading) {
    return <Text size="sm">Carregando avaliação IA...</Text>;
  }

  if (error) {
    return <Text size="sm">Não foi possivel carregar a avaliação IA.</Text>;
  }

  if (!avaliacao) {
    return <Text size="sm">Nenhuma avaliação IA encontrada.</Text>;
  }

  const percentual = typeof avaliacao.scorePercentual === "number" ? avaliacao.scorePercentual : null;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Stack gap={2}>
          <Text fw={600}>Justificativas retornadas pela IA</Text>
          <Text size="xs" c="dimmed">
            Matriz de Critérios: {avaliacao.rubricaVersion}
          </Text>
        </Stack>
        <Badge color={STATUS_CONFIG[avaliacao.status].color}>{STATUS_CONFIG[avaliacao.status].label}</Badge>
      </Group>

      <Paper withBorder p="md">
        <Stack gap="xs">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={600}>
              Resultado
            </Text>
            <Badge color={avaliacao.conformeRegulamento ? "green" : "yellow"} variant="light">
              {avaliacao.conformeRegulamento ? "Conforme regulamento" : "Requer atencao"}
            </Badge>
          </Group>
          {percentual !== null && (
            <>
              <Group justify="space-between">
                <Text size="sm">Percentual</Text>
                <Text size="sm" fw={600}>
                  {percentual}%
                </Text>
              </Group>
              <Progress value={percentual} />
            </>
          )}
          <Group gap="xl">
            <Text size="sm">Score: {avaliacao.scoreCalculado}</Text>
            {typeof avaliacao.scoreMaximo === "number" && <Text size="sm">Max: {avaliacao.scoreMaximo}</Text>}
          </Group>
        </Stack>
      </Paper>

      <Divider label="Critérios avaliados" labelPosition="center" />

      {avaliacao.resultadoJson?.avaliacao?.length ? (
        <Stack gap="sm">
          {avaliacao.resultadoJson.avaliacao.map((item) => (
            <Paper key={item.criterio} withBorder p="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap={2} style={{ flex: 1 }}>
                  <Text size="sm" fw={600}>
                    {item.criterio}
                  </Text>
                  <Text size="sm">{item.justificativa}</Text>
                </Stack>
                <Stack gap={4} align="flex-end">
                  <Badge variant="light">Nota {item.nota}</Badge>
                  {item.incerteza && (
                    <Badge color="yellow" variant="light">
                      Incerto
                    </Badge>
                  )}
                </Stack>
              </Group>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">
          Sem justificativas disponiveis.
        </Text>
      )}

      {avaliacao.resultadoJson?.flags?.length ? (
        <Paper withBorder p="sm">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Flags
            </Text>
            <Group gap="xs" wrap="wrap">
              {avaliacao.resultadoJson.flags.map((flag) => (
                <Badge key={flag} variant="outline">
                  {flag}
                </Badge>
              ))}
            </Group>
          </Stack>
        </Paper>
      ) : null}

      {avaliacao.resultadoJson?.perguntas?.length ? (
        <Paper withBorder p="sm">
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Perguntas sugeridas
            </Text>
            <Stack gap={4}>
              {avaliacao.resultadoJson.perguntas.map((pergunta) => (
                <Text size="sm" key={pergunta}>
                  • {pergunta}
                </Text>
              ))}
            </Stack>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
}

export default AvaliacaoIAModalContent;
export { STATUS_CONFIG };
